import React, { ClipboardEvent } from "react"
import {
  Flex, IconButton, Input, InputGroup, InputLeftElement, InputRightElement,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import CopyIconButton from "components/elements/CopyIconButton"
import CopyToClipboard from "components/elements/CopyToClipboard"
import { removeProtocolFromUrl } from "utilities/strings"

// Copy is a fixed width so it doesn't change size when it says Copied. Room for the
// buttons: Copy 5rem + gap 0.25rem + Open 2rem + padding 0.375rem + 0.5rem spare.
const COPY_WIDTH = "5rem"
const BUTTONS_WIDTH = "8.125rem"

type Props = {
  url: string
  withOpen?: boolean
}

const copyFullUrl = (url: string) => (event: ClipboardEvent<HTMLInputElement>) => {
  const { selectionStart, selectionEnd, value } = event.currentTarget

  if (selectionStart === 0 && selectionEnd === value.length) {
    event.clipboardData.setData("text/plain", url)
    event.preventDefault()
  }
}

const LinkField = ({ url, withOpen }: Props) => {
  if (withOpen) {
    return (
      <InputGroup size="sm" data-testid="link-field-group">
        <InputLeftElement pointerEvents="none" color="gray.500" h={10}>
          <Icon icon="link-chain" size={4} />
        </InputLeftElement>
        <Input
          value={removeProtocolFromUrl(url)}
          isReadOnly
          h={10}
          borderRadius="md"
          bg="gray.50"
          borderColor="gray.300"
          fontWeight="semibold"
          textOverflow="ellipsis"
          paddingEnd={BUTTONS_WIDTH}
          aria-label="Link"
          onCopy={copyFullUrl(url)}
        />
        <InputRightElement w="auto" h={10} pr={1.5}>
          <Flex gap={1}>
            <CopyToClipboard
              text={url}
              size="sm"
              variant="outline"
              colorScheme="gray"
              bg="white"
              leftIcon="copy"
              iconSpacing={1.5}
              px={2.5}
              w={COPY_WIDTH}
              copiedContent="Copied"
            >
              Copy
            </CopyToClipboard>
            <IconButton
              as="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              variant="outline"
              bg="white"
              aria-label="Open event link"
              icon={<Icon icon="link-external" size={3} />}
            />
          </Flex>
        </InputRightElement>
      </InputGroup>
    )
  }

  return (
    <InputGroup>
      <InputLeftElement pointerEvents="none" color="gray.500">
        <Icon icon="link-chain" size={4} />
      </InputLeftElement>
      <Input
        value={url}
        isReadOnly
        bg="gray.50"
        borderColor="gray.300"
        fontWeight="semibold"
        pr={10}
        aria-label="Link"
      />
      <InputRightElement>
        <CopyIconButton text={url} />
      </InputRightElement>
    </InputGroup>
  )
}

export default LinkField
